import type { CartesianPose } from '@sb/geometry';
import { additionBetweenPoses, invertPose } from '@sb/geometry';

/**
 * Resolve the inputs to sphere-radius trim selection in a single anchor frame,
 * so the runner (`loadRecordingWindow`) and the visualizer (`useVisualizeRun
 * DemonstrationPlan`) cannot drift in how they decide which recorded frames
 * are "in-window."
 *
 * The trim has two questions:
 *   1. Where is the sphere centered? → `pin` picks the anchor.
 *   2. In what frame is the distance measured? → the anchor's taught frame
 *      (wrist vs ee-*) decides.
 *
 * Then every leg of the trim — the per-frame points (`framePositions`), the
 * sphere center (`sphereCenter`), and the displayed path — is in the same
 * anchor frame. That's the invariant this helper enforces.
 *
 * `tcp` resolution: prefer `recordTcp` (the gripper offset captured at record
 * time, lifted to base coords). Fall back to `liveTcp` (the current
 * equipment's offset) for legacy recordings without `gripper_tcp_*` metadata.
 * Using `recordTcp` makes the trim invariant to gripper swaps — the same
 * recording-frame indices are selected regardless of what gripper is on now.
 * The runner's `frameTransform = recordTcp · inv(liveTcp)` handles the live-
 * gripper compensation separately at replay time.
 */
export type RecordedFrameKind = 'wrist' | 'tcp';

export interface SphereScanInputs {
  pin: 'start' | 'end' | 'reference';
  frames: CartesianPose[];

  // Recording's stored reference pose and the frame it was taught in.
  refWorldPose: CartesianPose | undefined;
  refTcpOption: string | undefined;

  // Picked target's taught frame ('wrist' | 'ee-*' | …). Applies to
  // pin=start/end. For pin=reference the anchor frame comes from
  // `refTcpOption` instead.
  pickedTcpOption: string | undefined;

  // The recording's native frame. echo-bot writes flange poses (wrist) when
  // its `tcp_offset_pose` config is zero. A non-zero `tcp_offset_pose`
  // produces TCP-frame frames.
  recordedFrame: RecordedFrameKind;

  // Record-time gripper offset (wrist→TCP, base-aligned). Undefined when the
  // recording predates `gripper_tcp_*` metadata.
  recordTcp: CartesianPose | undefined;

  // Live equipment offset (wrist→TCP, base-aligned). Fallback when
  // `recordTcp` is missing; never the preferred source for trim selection.
  liveTcp: CartesianPose | undefined;
}

export interface SphereScanOutputs {
  framePositions: CartesianPose[];
  sphereCenter: CartesianPose;
  side: 'first' | 'last';
}

function anchorIsTcp(input: SphereScanInputs): boolean {
  // pin=reference: the recording's stored reference frame when it has one,
  // else the frame of the reference entry being re-grounded onto at runtime
  // — locate outputs tag their entries `tcpOption: 'ee-any'`, so a
  // locate-grounded replay measures distance in the tool frame.
  const tcpOption =
    input.pin === 'reference'
      ? input.refTcpOption ?? input.pickedTcpOption
      : input.pickedTcpOption;

  return tcpOption !== undefined && tcpOption.startsWith('ee-');
}

export function resolveSphereScan(
  input: SphereScanInputs,
): SphereScanOutputs | null {
  const { pin, frames, refWorldPose, recordedFrame, recordTcp, liveTcp } =
    input;

  if (frames.length === 0) return null;

  const tcp = recordTcp ?? liveTcp;
  const wantTcp = anchorIsTcp(input);

  // Convert recorded frames into the anchor frame. The four cases of the
  // matrix collapse to two conversions: identity, or one `· tcp` / `· inv(tcp)`.
  let framePositions: CartesianPose[];

  if (wantTcp && recordedFrame === 'wrist') {
    if (!tcp) return null;
    framePositions = frames.map((f) => additionBetweenPoses(f, tcp));
  } else if (!wantTcp && recordedFrame === 'tcp') {
    if (!tcp) return null;

    framePositions = frames.map((f) =>
      additionBetweenPoses(f, invertPose(tcp)),
    );
  } else {
    framePositions = frames;
  }

  let sphereCenter: CartesianPose;

  if (pin === 'start') {
    sphereCenter = framePositions[0]!;
  } else if (pin === 'end') {
    sphereCenter = framePositions[framePositions.length - 1]!;
  } else if (refWorldPose) {
    sphereCenter = refWorldPose;
  } else {
    sphereCenter = framePositions[framePositions.length - 1]!;
  }

  const side: 'first' | 'last' = pin === 'start' ? 'first' : 'last';

  return { framePositions, sphereCenter, side };
}
